import React, { useEffect } from 'react';
import { Admin, Resource, CustomRoutes } from 'react-admin';
import { ContactList, ContactEdit, ContactCreate } from './resources/contacts';
import { CalendarList, CalendarEdit, CalendarCreate } from './resources/calendars';
import { UserList, UserEdit, UserCreate } from './resources/users';
import { TeamList, TeamEdit, TeamCreate } from './resources/teams';
import { CallHistoryList } from './resources/callHistory';
import { SystemLogsList } from './resources/systemLogs';
import { Dashboard } from './Dashboard';
import { authProvider } from './authProvider';
import { dataProvider } from './dataProvider';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import EventIcon from '@mui/icons-material/Event';
import PeopleIcon from '@mui/icons-material/People';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import HistoryIcon from '@mui/icons-material/History';
import AssignmentIcon from '@mui/icons-material/Assignment';
import Layout from './Layout';
import LoginPage from './LoginPage';
import { Route } from 'react-router-dom';
import { QueryClientProvider } from 'react-query';
import { queryClient, resetQueryCacheForTenantChange } from './queryClient';
// Import tenant utilities
import { extractTenantFromHostname, hasTenantChanged } from './utils/tenant';
// Import UserSettingsPage component
import UserSettingsPage from './components/UserSettingsPage';
// Import FastAuthCheck for immediate auth validation
import { FastAuthCheck } from './FastAuthCheck';

const App = () => {
  // Handle tenant changes on app initialization
  useEffect(() => {
    // Extract and store current tenant
    const currentTenant = extractTenantFromHostname();
    console.log(`App initialized with tenant: ${currentTenant}`);
    
    // Check if tenant has changed
    if (hasTenantChanged()) {
      console.log(`Tenant changed to: ${currentTenant}`);
      
      // Clear query cache when tenant changes
      resetQueryCacheForTenantChange();
      
      // Update localStorage with new tenant
      localStorage.setItem('tenant', currentTenant);
    }
    
    // Fast auth check on mount - immediately redirect if not authenticated
    const token = localStorage.getItem('token');
    if (!token && !window.location.hash.includes('/login')) {
      // No token and not already on login page - redirect immediately
      window.location.hash = '#/login';
    }
  }, []);

  return (
    <FastAuthCheck>
      <QueryClientProvider client={queryClient}>
        <Admin 
        dashboard={Dashboard}
        authProvider={authProvider}
        dataProvider={dataProvider}
        layout={Layout}
        loginPage={LoginPage}
        title="WhoYouGonnaCall Admin"
        darkTheme={{ palette: { mode: 'dark' } }}
      >
        {permissions => [
          // Resources available to all authenticated users
          <Resource 
            name="contacts" 
            list={ContactList} 
            edit={ContactEdit} 
            create={ContactCreate} 
            icon={ContactPhoneIcon}
            options={{ label: 'Contacts' }}
          />,
          <Resource 
            name="calendars" 
            list={CalendarList} 
            edit={CalendarEdit} 
            create={CalendarCreate} 
            icon={EventIcon}
            options={{ label: 'Calendars' }}
          />,
          <Resource
            name="callHistory"
            list={CallHistoryList}
            icon={HistoryIcon}
            options={{ label: 'Call History' }}
          />,
          <Resource
            name="systemLogs"
            list={SystemLogsList}
            icon={AssignmentIcon}
            options={{ label: 'System Logs' }}
          />,

          // Resources only available to tenant_admin users
          permissions === 'tenant_admin' ? (
            <Resource
              name="users"
              list={UserList}
              edit={UserEdit}
              create={UserCreate}
              icon={PeopleIcon}
              options={{ label: 'Users' }}
            />
          ) : null,

          permissions === 'tenant_admin' ? (
            <Resource
              name="teams"
              list={TeamList}
              edit={TeamEdit}
              create={TeamCreate}
              icon={GroupWorkIcon}
              options={{ label: 'Teams' }}
            />
          ) : null,
          
          
          // Custom routes
          <CustomRoutes key="settings-routes">
            {/* Route for user password change */}
            <Route path="/change-password" element={<UserSettingsPage />} />
          </CustomRoutes>
        ]}
        </Admin>
      </QueryClientProvider>
    </FastAuthCheck>
  );
};

export default App;